import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { GetPartnerDetailData } from "@/types/partners";
import { PlaceItem } from "@/types/place";

export enum ESidebarMenuType {
  SPATIAL = "spatial",
  DATASET = "dataset",
  DATASET_EXPLORER = "data_explorer",
  SEARCH = "search",
  ANALYSIS_HISTORY = "analysis_history"
}

export type SidebarMenuTypePopup =
  | ESidebarMenuType.SPATIAL
  | ESidebarMenuType.DATASET
  | ESidebarMenuType.DATASET_EXPLORER
  | ESidebarMenuType.SEARCH
  | ESidebarMenuType.ANALYSIS_HISTORY
  | null;

export interface SpatialState {
  allowPinMarker?: boolean;
  showAreaAdminPopup?: boolean;
  showPolygonPopup?: boolean;
  showCatchmentPopup?: boolean;
  showDatasetPopup?: boolean;
  showAnalysisSummaryPopup: boolean;
  showAnalysisResultPopup?: boolean;
  showAnalysisJobList?: boolean;
  showDataWeightSettingsPopup?: boolean;
  showDatasetExplorerPopup?: boolean;
  showSearchLocationPopup?: boolean;
  showGlobalFilterPopup?: boolean;
  markerInfo?: PlaceItem | string | null;
  showTemplateModal?: boolean;
  showSpatialAnalysisResultTabularModal: boolean;
  showComparisonPopup: {
    isShow: boolean;
    isFromAnalysisResult: boolean;
    isLimitShow: boolean;
  };
  sideBarMenuTypePopupDisplay?: SidebarMenuTypePopup;
  isExpandLegendVisualization?: boolean;
  showProjectList?: boolean;
  sidebarUserSettingPopupDisplay: {
    isPanelView: boolean;
    isPanelAdd: boolean;
    isPanelEdit: boolean;
  };
  isDrawPolygon?: boolean;
  isCustomDrawPolygonDataset?: boolean;
  showDrawLimitInfo?: boolean;
  sidebarMenu: boolean;
  showLegendInfo?: boolean;
  partnerData?: GetPartnerDetailData | null;
  showLoadingOverlay: boolean;
  showPanelDatasetDetail?: {
    isAddNewRow: boolean;
    isEditRow: boolean;
  };
  showNpsModal: boolean;
  npsMilestoneReached: string | null;
  npsId?: string;
  showTimeSeriesModal: boolean;
  showDatasetWidgetPopup: boolean;
}

export const uiInitialState: SpatialState = {
  allowPinMarker: false,
  showAreaAdminPopup: false,
  showCatchmentPopup: false,
  showDatasetPopup: false,
  showPolygonPopup: false,
  showAnalysisResultPopup: false,
  showAnalysisSummaryPopup: false,
  showSearchLocationPopup: false,
  showGlobalFilterPopup: false,
  markerInfo: null,
  showDataWeightSettingsPopup: false,
  showSpatialAnalysisResultTabularModal: false,
  showTemplateModal: false,
  showComparisonPopup: {
    isShow: false,
    isFromAnalysisResult: false,
    isLimitShow: false
  },
  sideBarMenuTypePopupDisplay: null,
  isExpandLegendVisualization: false,
  showProjectList: false,
  sidebarUserSettingPopupDisplay: {
    isPanelView: false,
    isPanelAdd: false,
    isPanelEdit: false
  },
  sidebarMenu: false,
  showLegendInfo: false,
  partnerData: null,
  isDrawPolygon: false,
  showDrawLimitInfo: false,
  showLoadingOverlay: false,
  showAnalysisJobList: false,
  showPanelDatasetDetail: {
    isAddNewRow: false,
    isEditRow: false
  },
  isCustomDrawPolygonDataset: false,
  showNpsModal: false,
  npsMilestoneReached: null,
  showTimeSeriesModal: false,
  showDatasetWidgetPopup: false
};

export const uiSlice = createSlice({
  name: "ui",
  initialState: uiInitialState,
  reducers: {
    toggleAllowPinMarker: (state, action: PayloadAction<boolean>) => {
      state.allowPinMarker = action.payload;
    },

    toggleAreaAdminPopup: (state, action: PayloadAction<boolean>) => {
      state.showAreaAdminPopup = action.payload;
    },
    toggleCatchmentPopup: (state, action: PayloadAction<boolean>) => {
      state.showCatchmentPopup = action.payload;
    },
    toggleDatasetExplorerPopup: (state, action: PayloadAction<boolean>) => {
      state.showDatasetExplorerPopup = action.payload;
    },
    toggleDataWeightSettingsPopup: (state, action: PayloadAction<boolean>) => {
      state.showDataWeightSettingsPopup = action.payload;
    },
    toggleDatasetPopup: (state, action: PayloadAction<boolean>) => {
      state.showDatasetPopup = action.payload;
    },
    toggleAnalysisResultPopup: (state) => {
      state.showAnalysisResultPopup = !state.showAnalysisResultPopup;
    },
    toggleComparisonLimitShow: (state) => {
      state.showComparisonPopup.isLimitShow =
        !state.showComparisonPopup.isLimitShow;
    },
    toggleLegendInfo: (state) => {
      state.showLegendInfo = !state.showLegendInfo;
    },
    toggleLegendVisualization: (state) => {
      state.isExpandLegendVisualization = !state.isExpandLegendVisualization;
    },
    toggleShowProjectList: (state, action: PayloadAction<boolean>) => {
      state.showProjectList = action.payload;
    },
    toggleSpatialAnalysisResultTabularModal: (state) => {
      state.showSpatialAnalysisResultTabularModal =
        !state.showSpatialAnalysisResultTabularModal;
    },
    toggleAnalysisJobList: (state) => {
      state.showAnalysisJobList = !state.showAnalysisJobList;
    },
    toggleGlobalFilterPopup: (state, action: PayloadAction<boolean>) => {
      state.showGlobalFilterPopup = action.payload;
    },
    setShowTemplateModal: (state, action: PayloadAction<boolean>) => {
      state.showTemplateModal = action.payload;
    },
    setShowAnalysisSummaryPopup: (state, action: PayloadAction<boolean>) => {
      state.showAnalysisSummaryPopup = action.payload;
    },
    setShowDrawLimitInfo: (state, action: PayloadAction<boolean>) => {
      state.showDrawLimitInfo = action.payload;
    },
    setAnalysisResultPopup: (state, action: PayloadAction<boolean>) => {
      state.showAnalysisResultPopup = action.payload;
    },
    setActiveSpatialAnalysisPanel: (state, action: PayloadAction<string>) => {
      const activePanel = action.payload;

      state.showCatchmentPopup = activePanel === "showCatchmentPopup";
      state.showAreaAdminPopup = activePanel === "showAreaAdminPopup";
      state.showPolygonPopup = activePanel === "showPolygonPopup";
      state.showDataWeightSettingsPopup =
        activePanel === "showDataWeightSettingsPopup";
      state.showDatasetExplorerPopup =
        activePanel === "showDatasetExplorerPopup";
      state.showDatasetPopup = activePanel === "showDatasetPopup";
      state.showGlobalFilterPopup = activePanel === "showGlobalFilterPopup";
    },
    setSideBarMenuTypePopupDisplay: (
      state,
      action: PayloadAction<SidebarMenuTypePopup>
    ) => {
      state.sideBarMenuTypePopupDisplay = action.payload;
    },
    setShowComparisonPopup: (
      state,
      action: PayloadAction<{
        isShow: boolean;
        isFromAnalysisResult: boolean;
        isLimitShow: boolean;
      }>
    ) => {
      state.showComparisonPopup = action.payload;
    },
    disableDrawPolygon: (_state, action: PayloadAction<boolean>) => {
      return {
        ...uiInitialState,
        sideBarMenuTypePopupDisplay: ESidebarMenuType.SPATIAL,
        showPolygonPopup: !action.payload,
        isDrawPolygon: false,
        showCatchmentPopup: action.payload
      };
    },
    enableDrawPolygon: () => {
      return {
        ...uiInitialState,
        isDrawPolygon: true
      };
    },
    setShowPolygonPopup: (state, action: PayloadAction<boolean>) => {
      state.showPolygonPopup = action.payload;
    },
    setSidebarUserSettingPopupDisplay: (
      state,
      action: PayloadAction<{
        isPanelView: boolean;
        isPanelAdd: boolean;
        isPanelEdit: boolean;
      }>
    ) => {
      state.sidebarUserSettingPopupDisplay = action.payload;
    },
    resetSidebarUserSettingPopupDisplay: (state) => {
      state.sidebarUserSettingPopupDisplay = {
        isPanelView: false,
        isPanelAdd: false,
        isPanelEdit: false
      };
    },
    toggleSidebarMenu: (state, action: PayloadAction<boolean>) => {
      state.sidebarMenu = action.payload;
    },
    setUIDataConfig: (state, action: PayloadAction<SpatialState>) => {
      return {
        ...state,
        ...action.payload
      };
    },
    resetUIDataConfig: () => uiInitialState,
    resetUIDataConfigExceptSidebarMenu: (state) => {
      return {
        ...uiInitialState,
        sidebarMenu: state.sidebarMenu
      };
    },
    setPartnerData: (
      state,
      action: PayloadAction<GetPartnerDetailData | null>
    ) => {
      state.partnerData = action.payload;
    },
    toggleShowSearchLocationPopup: (state) => {
      state.showSearchLocationPopup = !state.showSearchLocationPopup;
    },
    setMarkerInfo: (
      state,
      action: PayloadAction<PlaceItem | string | null>
    ) => {
      state.markerInfo = action.payload;
    },
    setLoadingOverlay: (state, action: PayloadAction<boolean>) => {
      state.showLoadingOverlay = action.payload;
    },
    resetShowPanelDatasetDetail: (state) => {
      state.showPanelDatasetDetail = {
        isAddNewRow: false,
        isEditRow: false
      };
    },
    setShowPanelDatasetDetail: (
      state,
      action: PayloadAction<{
        isAddNewRow: boolean;
        isEditRow: boolean;
      }>
    ) => {
      state.showPanelDatasetDetail = action.payload;
    },
    enabledCustomDrawPolygonDataset: (state) => {
      state.isCustomDrawPolygonDataset = true;
    },
    disabledCustomDrawPolygonDataset: (state) => {
      state.isCustomDrawPolygonDataset = false;
    },
    setShowNpsModal: (state, action: PayloadAction<boolean>) => {
      state.showNpsModal = action.payload;
    },
    setNpsMilestoneReached: (state, action: PayloadAction<string | null>) => {
      state.npsMilestoneReached = action.payload;
    },
    setNpsId: (state, action: PayloadAction<string>) => {
      state.npsId = action.payload;
    },
    setShowTimeSeriesModal: (state, action: PayloadAction<boolean>) => {
      state.showTimeSeriesModal = action.payload;
    },
    toggleDatasetWidgetPopup: (state) => {
      state.showDatasetWidgetPopup = !state.showDatasetWidgetPopup;
    }
  }
});

export const {
  toggleAllowPinMarker,
  toggleAreaAdminPopup,
  toggleCatchmentPopup,
  toggleDatasetPopup,
  toggleDataWeightSettingsPopup,
  toggleSpatialAnalysisResultTabularModal,
  toggleComparisonLimitShow,
  toggleAnalysisResultPopup,
  toggleDatasetExplorerPopup,
  toggleLegendVisualization,
  toggleShowProjectList,
  toggleLegendInfo,
  toggleSidebarMenu,
  toggleAnalysisJobList,
  toggleGlobalFilterPopup,
  resetSidebarUserSettingPopupDisplay,
  resetUIDataConfig,
  resetUIDataConfigExceptSidebarMenu,
  setUIDataConfig,
  setPartnerData,
  setShowPolygonPopup,
  setShowComparisonPopup,
  setSidebarUserSettingPopupDisplay,
  setActiveSpatialAnalysisPanel,
  setSideBarMenuTypePopupDisplay,
  setShowAnalysisSummaryPopup,
  setShowDrawLimitInfo,
  setAnalysisResultPopup,
  toggleShowSearchLocationPopup,
  setMarkerInfo,
  enableDrawPolygon,
  disableDrawPolygon,
  setShowTemplateModal,
  setLoadingOverlay,
  resetShowPanelDatasetDetail,
  setShowPanelDatasetDetail,
  enabledCustomDrawPolygonDataset,
  disabledCustomDrawPolygonDataset,
  setShowNpsModal,
  setNpsMilestoneReached,
  setNpsId,
  setShowTimeSeriesModal,
  toggleDatasetWidgetPopup
} = uiSlice.actions;

export const uiReducer = uiSlice.reducer;
